import React from "react";
import { iconMap } from "./GraphVizConstants";
import "./OrgGraphLegend.css";
import { WorkOutline } from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import {
  LEASED_CARS_BADGE_COLOR,
  LEASED_CARS_ICON,
  TENANT_CASE_BADGE_COLOR,
} from "./lib/badgeUtils";

interface LegendProps {
  enableCompanyComments?: boolean;
  showCompanyComments?: boolean;
  onToggleCompanyComments?: () => void;
  enableIvoLicenses?: boolean;
  enableLeasedCars?: boolean;
  enableTenantCases?: boolean;
}

const Legend = ({
  enableCompanyComments = false,
  showCompanyComments = true,
  onToggleCompanyComments,
  enableIvoLicenses = false,
  enableLeasedCars = false,
  enableTenantCases = false,
}: LegendProps) => {
  const { t, i18n } = useTranslation();
  const tenantCasesLabel = t('graph.directTenantCases', {
    defaultValue: i18n.language?.startsWith('sv')
      ? 'Direkt kopplad till ett ärende'
      : 'Directly linked to a case',
  });
  // Function to sort entries alphabetically by description
  const sortedCompanyEntries = Object.entries(iconMap.Company).sort((a, b) => {
    return t(`graph.legendItems.${a[0]}`).localeCompare(t(`graph.legendItems.${b[0]}`), i18n.language);
  });

  return (
    <div style={{ padding: '10px' }}>
      {enableCompanyComments && onToggleCompanyComments && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={onToggleCompanyComments}
            style={{
              border: '1px solid #2563eb',
              backgroundColor: showCompanyComments ? '#2563eb' : 'transparent',
              color: showCompanyComments ? '#ffffff' : 'inherit',
              borderRadius: '999px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t(showCompanyComments ? 'graph.hideComments' : 'graph.showComments')}
          </button>
        </div>
      )}
      <table className="legend-table">
        <thead>
          <tr>
            <th>{t('graph.icon')}</th>
            <th>{t('common.fields.description')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <i className="fa" style={{ color: iconMap.FunktionarPerson.shapeColor }}>
                {iconMap.FunktionarPerson.code}
              </i>
            </td>
            <td>{t('graph.legendItems.FunktionarPerson')}</td>
          </tr>
          {sortedCompanyEntries.map(([key, value]) => (
            <tr key={key}>
              <td>
                <i className="fa" style={{ color: value.shapeColor }}>
                  {value.code}
                </i>
              </td>
              <td>{t(`graph.legendItems.${key}`, { defaultValue: value.description })}</td>
            </tr>
          ))}
          <tr>
            <td>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#6c757d',
                borderRadius: '50%',
                border: '2px solid #ffffff',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '12px',
                margin: '0 auto',
                boxShadow: '0 0 2px rgba(0,0,0,0.3)'
              }}>#</div>
            </td>
            <td>{t('graph.nextLayerCompanies')}</td>
          </tr>
          <tr>
            <td>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#dc3545',
                borderRadius: '50%',
                border: '2px solid #ffffff',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '12px',
                margin: '0 auto',
                boxShadow: '0 0 2px rgba(0,0,0,0.3)'
              }}>#</div>
            </td>
            <td>{t('graph.nextLayerDebtCompanies')}</td>
          </tr>
          <tr>
            <td>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#f39c12', // Warning/Watchlist color
                borderRadius: '50%',
                border: '2px solid #ffffff',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px',
                margin: '0 auto',
                boxShadow: '0 0 2px rgba(0,0,0,0.3)'
              }}>!</div>
            </td>
            <td>{t('graph.onWatchlist')}</td>
          </tr>
          {enableCompanyComments && (
            <tr>
              <td>
                <div style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#2563eb',
                  borderRadius: '50%',
                  border: '2px solid #ffffff',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  margin: '0 auto',
                  boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                }}>C</div>
              </td>
              <td>{t('graph.hasComment')}</td>
            </tr>
          )}
          <tr>
            <td><div style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: 24, height: 24, margin: '0 auto', display: 'grid', placeItems: 'center' }}><WorkOutline sx={{ fontSize: 14 }} /></div></td>
            <td>{t('employmentSupport.badge')}</td>
          </tr>
          {enableIvoLicenses && (
            <tr>
              <td>
                <div style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#0f766e',
                  borderRadius: '50%',
                  border: '2px solid #ffffff',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  margin: '0 auto',
                  boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                }}>L</div>
              </td>
              <td>{t('graph.hasIvoLicense')}</td>
            </tr>
          )}
          {enableLeasedCars && (
            <tr>
              <td>
                <div style={{
                  width: '78px',
                  height: '24px',
                  backgroundColor: LEASED_CARS_BADGE_COLOR,
                  borderRadius: '999px',
                  border: '2px solid #ffffff',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  margin: '0 auto',
                  boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                }}>
                  <i className="fa" style={{ color: '#ffffff', fontSize: '10px' }}>{LEASED_CARS_ICON}</i>
                  <span>100 (150)</span>
                </div>
              </td>
              <td>{t('graph.leasedCars')}</td>
            </tr>
          )}
          {enableTenantCases && (
            <tr>
              <td>
                <div style={{
                  minWidth: '38px',
                  height: '24px',
                  backgroundColor: TENANT_CASE_BADGE_COLOR,
                  borderRadius: '999px',
                  border: '2px solid #ffffff',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  padding: '0 5px',
                  fontSize: '11px',
                  fontWeight: 700,
                  margin: '0 auto',
                  boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                }}>
                  <svg width="11" height="13" viewBox="0 0 12 14" aria-hidden="true">
                    <path d="M2 1.5h5l3 3v8H2z M7 1.5v3h3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  </svg>
                  <span>2</span>
                </div>
              </td>
              <td>{tenantCasesLabel}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Legend;
